import { useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

const LOGO_URL = 'https://public.readdy.ai/ai/img_res/2f4f4ce3-8121-45e6-bf04-63921ac249c0.png';

const legalDocs: Record<string, { title: string; icon: string; description: string }> = {
  'aviso-de-privacidad': {
    title: 'Aviso de Privacidad',
    icon: 'ri-shield-user-line',
    description:
      'En MF Dental estamos comprometidos con la protección de tus datos personales. El presente aviso describe cómo recopilamos, utilizamos y protegemos tu información conforme a la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP).',
  },
  'terminos-y-condiciones': {
    title: 'Términos y Condiciones',
    icon: 'ri-file-list-3-line',
    description:
      'Los presentes Términos y Condiciones regulan el uso del sitio web y los servicios ofrecidos por MF Dental. Al acceder y utilizar nuestros servicios, aceptas cumplir con las condiciones aquí establecidas.',
  },
  'condiciones-generales': {
    title: 'Compromiso General de Condiciones',
    icon: 'ri-file-paper-2-line',
    description:
      'El presente documento establece el compromiso general de condiciones bajo el cual MF Dental ofrece sus servicios de atención odontológica, definiendo los derechos y obligaciones de ambas partes.',
  },
  'descarga-de-responsabilidades': {
    title: 'Descarga General de Responsabilidades',
    icon: 'ri-file-warning-line',
    description:
      'El presente documento establece los límites de responsabilidad de MF Dental respecto a la prestación de sus servicios odontológicos, informando al paciente sobre los alcances y condiciones de cada tratamiento.',
  },
};

const PLACEHOLDER_SECTIONS = [
  'Definiciones y alcance del documento',
  'Responsable del tratamiento de datos',
  'Finalidad y uso de la información',
  'Derechos del titular (ARCO)',
  'Cambios y actualizaciones',
  'Contacto y aclaraciones',
];

export default function LegalPage() {
  const { tipo } = useParams<{ tipo: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [tipo]);

  const doc = tipo ? legalDocs[tipo] : null;

  if (!doc) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-2xl mx-auto mb-4">
            <i className="ri-file-unknow-line text-gray-400 text-3xl" />
          </div>
          <h1 className="text-2xl font-bold text-[#0A2540] mb-2">Documento no encontrado</h1>
          <p className="text-gray-400 text-sm mb-6">El documento legal que buscas no existe.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-[#3A86FF] text-white px-6 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f9fc] font-sans">
      {/* Top nav bar */}
      <header className="bg-[#0A2540] sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-3 cursor-pointer group"
          >
            <div className="w-8 h-8 flex items-center justify-center">
              <img src={LOGO_URL} alt="MF Dental" className="h-8 w-auto object-contain" />
            </div>
            <span className="text-white font-bold text-base group-hover:text-[#3A86FF] transition-colors">
              MF Dental
            </span>
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/60 hover:text-white text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line" />
            </div>
            Volver al sitio
          </button>
        </div>
      </header>

      {/* Hero banner */}
      <div className="bg-gradient-to-br from-[#0A2540] to-[#0d3060] py-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-[#3A86FF]/20 rounded-2xl mx-auto mb-5 border border-[#3A86FF]/30">
            <i className={`${doc.icon} text-[#3A86FF] text-3xl`} />
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
            {doc.title}
          </h1>
          <p className="text-white/60 text-base max-w-2xl mx-auto leading-relaxed">
            {doc.description}
          </p>
          <div className="flex items-center justify-center gap-2 mt-6 text-white/35 text-xs">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-time-line" />
            </div>
            Última actualización: {new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
      </div>

      {/* Document content area */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
          {/* Placeholder notice */}
          <div className="bg-amber-50 border-b border-amber-100 px-8 py-4 flex items-center gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-information-line text-amber-500 text-lg" />
            </div>
            <p className="text-amber-700 text-sm">
              <strong>Documento pendiente:</strong> El contenido completo de este documento legal será publicado próximamente.
            </p>
          </div>

          <div className="px-8 py-10">
            {/* Index */}
            <div className="bg-[#f7f9fc] rounded-2xl p-6 mb-10 border border-gray-100">
              <h2 className="text-[#0A2540] font-bold text-base mb-4 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-list-ordered text-[#3A86FF]" />
                </div>
                Índice de contenido
              </h2>
              <ol className="space-y-2">
                {PLACEHOLDER_SECTIONS.map((section, i) => (
                  <li key={section} className="flex items-center gap-3 text-sm text-gray-500">
                    <span className="w-6 h-6 flex items-center justify-center bg-[#3A86FF]/10 text-[#3A86FF] rounded-full text-xs font-bold flex-shrink-0">
                      {i + 1}
                    </span>
                    {section}
                  </li>
                ))}
              </ol>
            </div>

            {/* Content placeholder */}
            <div className="space-y-8">
              {PLACEHOLDER_SECTIONS.map((section, i) => (
                <div key={section} className="border-b border-gray-50 pb-8 last:border-0 last:pb-0">
                  <h3 className="text-[#0A2540] font-bold text-lg mb-3 flex items-center gap-3">
                    <span className="w-7 h-7 flex items-center justify-center bg-[#3A86FF]/10 text-[#3A86FF] rounded-lg text-sm font-bold flex-shrink-0">
                      {i + 1}
                    </span>
                    {section}
                  </h3>
                  <div className="space-y-2 pl-10">
                    <div className="h-3 bg-gray-100 rounded-full w-full" />
                    <div className="h-3 bg-gray-100 rounded-full w-5/6" />
                    <div className="h-3 bg-gray-100 rounded-full w-4/6" />
                  </div>
                </div>
              ))}
            </div>

            {/* Contact block */}
            <div className="mt-10 bg-[#0A2540] rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <p className="text-white font-semibold text-sm mb-1">¿Tienes preguntas sobre este documento?</p>
                <p className="text-white/50 text-xs">Contáctanos directamente, estamos para ayudarte.</p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <a
                  href="tel:5534748982"
                  className="flex items-center gap-2 border border-white/20 hover:border-[#3A86FF] text-white/70 hover:text-[#3A86FF] px-4 py-2 rounded-full text-sm whitespace-nowrap cursor-pointer transition-all"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-line" />
                  </div>
                  55 3474 8982
                </a>
                <a
                  href="https://wa.me/525534748982"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-[#25D366] hover:bg-[#1eaa52] text-white px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-whatsapp-line" />
                  </div>
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Back to home */}
        <div className="flex justify-center mt-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#3A86FF] hover:text-[#0A2540] text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line" />
            </div>
            Volver al sitio principal
          </button>
        </div>
      </div>

      {/* Mini footer */}
      <footer className="bg-[#0A2540] mt-8 py-5 px-6 text-center">
        <p className="text-white/30 text-xs">
          © {new Date().getFullYear()} MF Dental · Av. 504 #51, San Juan de Aragón, CDMX · 55 3474 8982
        </p>
      </footer>
    </div>
  );
}
